<template>
  <el-container class="layout">
    <!-- 侧边栏 -->
    <el-aside :width="isCollapsed ? '64px' : '220px'" class="sidebar">
      <div class="logo">
        <span v-if="!isCollapsed">🏫 管理后台</span>
        <span v-else>🏫</span>
      </div>
      
      <el-menu
        :default-active="activeMenu"
        :collapse="isCollapsed"
        :collapse-transition="false"
        router
        background-color="#304156"
        text-color="#bfcbd9"
        active-text-color="#409EFF"
      >
        <template v-for="route in routes">
          <!-- 没有子菜单 -->
          <el-menu-item v-if="!route.children || route.children.length === 1" :key="route.path" :index="route.path">
            <i :class="route.meta.icon"></i>
            <span slot="title">{{ route.meta.title }}</span>
          </el-menu-item>
          
          <!-- 有子菜单 -->
          <el-submenu v-else :key="route.path" :index="route.path">
            <template slot="title">
              <i :class="route.meta.icon"></i>
              <span>{{ route.meta.title }}</span>
            </template>
            
            <el-menu-item
              v-for="child in route.children"
              :key="child.path"
              :index="route.path + '/' + child.path"
            >
              {{ child.meta.title }}
            </el-menu-item>
          </el-submenu>
        </template>
      </el-menu>
    </el-aside>
    
    <el-container class="main-container">
      <!-- 顶部导航 -->
      <el-header class="header">
        <div class="header-left">
          <div class="collapse-btn" @click="toggleSidebar">
            <i :class="isCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'"></i>
          </div>
          <Breadcrumb class="breadcrumb" />
        </div>
        
        <div class="header-right">
          <el-dropdown @command="handleCommand">
            <span class="user-info">
              <img :src="userInfo.avatar || defaultAvatar" alt="avatar" class="avatar">
              <span class="username">{{ userInfo.nickname || userInfo.username }}</span>
              <el-tag v-if="isMainAdmin" size="mini" type="danger" effect="plain" style="margin-right:6px;">主管理员</el-tag>
              <i class="el-icon-arrow-down"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="profile"><i class="el-icon-user"></i> 个人中心</el-dropdown-item>
              <el-dropdown-item command="password"><i class="el-icon-lock"></i> 修改密码</el-dropdown-item>
              <el-dropdown-item divided command="logout"><i class="el-icon-switch-button"></i> 退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </el-header>
      
      <!-- 标签页 -->
      <div class="tags-view">
        <div class="tags-wrapper">
          <router-link
            v-for="tag in visitedViews"
            :key="tag.path"
            :to="tag.path"
            :class="['tag-item', { active: isActive(tag) }]"
          >
            {{ tag.title }}
            <span v-if="!isAffix(tag)" class="close-icon" @click.prevent.stop="closeSelectedTag(tag)">×</span>
          </router-link>
        </div>
      </div>
      
      <!-- 主内容区 -->
      <el-main class="main">
        <router-view />
      </el-main>
    </el-container>

    <!-- 个人中心 -->
    <el-dialog title="个人中心" :visible.sync="profileVisible" width="420px">
      <el-descriptions :column="1" border size="small">
        <el-descriptions-item label="用户名">{{ userInfo.username }}</el-descriptions-item>
        <el-descriptions-item label="姓名">{{ userInfo.nickname }}</el-descriptions-item>
        <el-descriptions-item label="角色">{{ isMainAdmin ? '主管理员（可创建子管理员）' : '管理员' }}</el-descriptions-item>
        <el-descriptions-item label="用户ID">{{ userInfo.id }}</el-descriptions-item>
      </el-descriptions>
      <span slot="footer">
        <el-button type="primary" @click="profileVisible = false">关 闭</el-button>
      </span>
    </el-dialog>

    <!-- 修改密码 -->
    <el-dialog title="修改密码" :visible.sync="pwdVisible" width="440px" @closed="resetPwdForm">
      <el-form ref="pwdForm" :model="pwdForm" :rules="pwdRules" label-width="96px">
        <el-form-item label="原密码" prop="oldPassword">
          <el-input v-model="pwdForm.oldPassword" type="password" show-password autocomplete="off" />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="pwdForm.newPassword" type="password" show-password autocomplete="off" />
        </el-form-item>
        <el-form-item label="确认新密码" prop="confirmPassword">
          <el-input v-model="pwdForm.confirmPassword" type="password" show-password autocomplete="off" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="pwdVisible = false">取 消</el-button>
        <el-button type="primary" :loading="pwdSubmitting" @click="submitChangePassword">确 定</el-button>
      </span>
    </el-dialog>
  </el-container>
</template>

<script>
import { mapGetters } from 'vuex'
import Breadcrumb from '@/components/Breadcrumb'
import { changePassword } from '@/api/user'

export default {
  name: 'Layout',
  components: { Breadcrumb },
  data() {
    const validateConfirm = (rule, value, callback) => {
      if (value !== this.pwdForm.newPassword) {
        callback(new Error('两次输入的新密码不一致'))
      } else {
        callback()
      }
    }
    return {
      defaultAvatar: 'https://wpimg.wallstcn.com/f778738c-e4f8-4870-b634-56703b4acafe.gif',
      routes: [],
      visitedViews: [],
      affixTags: [],
      profileVisible: false,
      pwdVisible: false,
      pwdSubmitting: false,
      pwdForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      pwdRules: {
        oldPassword: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, max: 64, message: '长度 6-64 位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          { validator: validateConfirm, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapGetters(['userInfo', 'sidebarCollapsed', 'isMainAdmin']),
    isCollapsed() {
      return this.sidebarCollapsed
    },
    activeMenu() {
      const route = this.$route
      const { meta, path } = route
      if (meta.activeMenu) {
        return meta.activeMenu
      }
      return path
    },
    // breadcrumb 由组件 `src/components/Breadcrumb` 内部计算，这里无需再算
  },
  watch: {
    $route: {
      immediate: true,
      handler(route) {
        this.addTags()
        this.moveToCurrentTag()
      }
    }
  },
  mounted() {
    this.initRoutes()
    this.initTags()
    this.addTags()
  },
  methods: {
    initRoutes() {
      const allRoutes = this.$router.options.routes.find(r => r.path === '/')
      this.routes = allRoutes && allRoutes.children ? allRoutes.children : []
    },
    toggleSidebar() {
      this.$store.dispatch('app/toggleSideBar')
    },
    handleCommand(command) {
      switch (command) {
        case 'profile':
          this.profileVisible = true
          break
        case 'password':
          this.resetPwdForm()
          this.pwdVisible = true
          break
        case 'logout':
          this.$confirm('确认退出登录?', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }).then(() => {
            this.$store.dispatch('user/logout')
            this.$router.replace('/login')
            this.$message.success('已退出登录')
          })
          break
      }
    },
    resetPwdForm() {
      this.pwdForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
      this.$nextTick(() => {
        if (this.$refs.pwdForm) this.$refs.pwdForm.clearValidate()
      })
    },
    submitChangePassword() {
      this.$refs.pwdForm.validate(valid => {
        if (!valid) return
        this.pwdSubmitting = true
        changePassword({
          oldPassword: this.pwdForm.oldPassword,
          newPassword: this.pwdForm.newPassword
        })
          .then(() => {
            this.$message.success('密码已修改，请重新登录')
            this.pwdVisible = false
            this.$store.dispatch('user/logout')
            this.$router.replace('/login')
          })
          .finally(() => {
            this.pwdSubmitting = false
          })
      })
    },
    isActive(route) {
      return route.path === this.$route.path
    },
    isAffix(tag) {
      return tag.meta && tag.meta.affix
    },
    filterAffixTags(routes, basePath = '/') {
      let tags = []
      routes.forEach(route => {
        if (route.meta && route.meta.affix) {
          const tagPath = basePath + route.path
          tags.push({
            fullPath: tagPath,
            path: tagPath,
            name: route.name,
            meta: { ...route.meta }
          })
        }
        if (route.children) {
          const tempTags = this.filterAffixTags(route.children, route.path + '/')
          if (tempTags.length >= 1) {
            tags = [...tags, ...tempTags]
          }
        }
      })
      return tags
    },
    initTags() {
      const allRoutes = this.$router.options.routes.find(r => r.path === '/')
      const affixTags = this.affixTags = this.filterAffixTags(allRoutes.children)
      for (const tag of affixTags) {
        if (tag.name) {
          this.visitedViews.push({
            path: tag.path,
            title: tag.meta.title || 'no-name'
          })
        }
      }
    },
    addTags() {
      const { name } = this.$route
      if (name) {
        const existingTag = this.visitedViews.find(v => v.path === this.$route.path)
        if (!existingTag) {
          this.visitedViews.push({
            path: this.$route.path,
            title: this.$route.meta.title || 'no-name'
          })
        }
      }
    },
    moveToCurrentTag() {
      // 标签滚动到当前
    },
    closeSelectedTag(view) {
      this.visitedViews = this.visitedViews.filter(v => v.path !== view.path)
      if (this.isActive(view)) {
        this.toLastView(this.visitedViews, view)
      }
    },
    toLastView(visitedViews, view) {
      const latestView = visitedViews.slice(-1)[0]
      if (latestView) {
        this.$router.push(latestView.path)
      } else {
        if (view.name === 'Dashboard') {
          this.$router.replace({ path: '/redirect' + view.fullPath })
        } else {
          this.$router.push('/')
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.layout {
  height: 100vh;
}

.sidebar {
  background-color: #304156;
  transition: width 0.3s;
  
  .logo {
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
    font-weight: 600;
    border-bottom: 1px solid #1f2d3d;
    
    span {
      overflow: hidden;
      white-space: nowrap;
    }
  }
  
  :deep(.el-menu) {
    border-right: none;
  }
}

.main-container {
  background: #f5f7fa;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  z-index: 1;
}

.header-left {
  display: flex;
  align-items: center;
}

.collapse-btn {
  font-size: 20px;
  cursor: pointer;
  padding: 0 15px;
  color: #666;
  
  &:hover {
    color: #409EFF;
  }
}

.breadcrumb {
  margin-left: 10px;
}

.header-right {
  .user-info {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 0 10px;
    
    .avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      margin-right: 10px;
    }
    
    .username {
      margin-right: 8px;
      color: #606266;
    }
  }
}

.tags-view {
  background: #fff;
  border-bottom: 1px solid #d8dce5;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.12), 0 0 3px 0 rgba(0, 0, 0, 0.04);
  
  .tags-wrapper {
    padding: 5px 10px;
    display: flex;
    gap: 5px;
  }
  
  .tag-item {
    display: inline-flex;
    align-items: center;
    height: 28px;
    line-height: 28px;
    padding: 0 12px;
    border: 1px solid #d8dce5;
    background: #fff;
    color: #495060;
    font-size: 12px;
    margin-left: 5px;
    border-radius: 3px;
    text-decoration: none;
    
    &:first-child {
      margin-left: 0;
    }
    
    &.active {
      background-color: #409EFF;
      color: #fff;
      border-color: #409EFF;
    }
    
    .close-icon {
      margin-left: 8px;
      width: 14px;
      height: 14px;
      line-height: 14px;
      text-align: center;
      border-radius: 50%;
      transition: all 0.3s;
      
      &:hover {
        background: rgba(0, 0, 0, 0.1);
      }
    }
  }
}

.main {
  padding: 20px;
  overflow-y: auto;
}

:deep(.el-menu-item i),
:deep(.el-submenu__title i) {
  margin-right: 10px;
}
</style>